/**
 * The DEV/E2E-only wallet-injection seam, extracted from `main.tsx` so it can
 * be unit-tested.
 *
 * It lets Playwright inject a deterministic `WalletConnector` double at the
 * connect seam (`lib/clients/cere-wallet/connect`). It is gated on `VITE_E2E`,
 * so the injection branch is live only under Playwright — never in normal dev,
 * and eliminated from the production build, where the flag is statically false
 * and the dynamic import below is dropped along with it.
 *
 * This lives in its own module for one reason: `main.tsx` renders the app as a
 * side effect of being imported, so nothing there can be tested without
 * booting the whole SPA. The only assurance the seam was inert used to be a
 * manual `grep __VAULT_E2E__ dist/assets/*.js` on a built bundle — which is not
 * a CI gate in this repo, and which nothing runs on future commits.
 *
 * Known gap, deliberately not closed here. This is a SINGLE-gate seam: one
 * build flag, then a window read. Marketplace's equivalent requires TWO
 * independent conditions (build flag AND per-page injection) and fails CLOSED
 * when only one is present, so it can never silently fall back to a real
 * wallet. That is the better shape and is filed as a follow-up rather than
 * redesigned inside an already-large PR.
 */

type Injected = { connector?: unknown };

/**
 * Reads the injected connector, or `undefined` when the flag is unset.
 *
 * `env` is `Record<string, unknown>` rather than `{ VITE_E2E?: unknown }` so
 * `import.meta.env` is assignable: Vite's generated `ImportMetaEnv` declares no
 * `VITE_E2E`, and TS rejects an object type with no properties in common.
 */
export function readInjectedConnector(env: Record<string, unknown>, win: unknown): unknown {
  // The flag is checked FIRST and independently of the window read: a stray
  // `window.__VAULT_E2E__` on a normal build must be inert, not merely
  // unlikely.
  if (!env.VITE_E2E) return undefined;
  return (win as { __VAULT_E2E__?: Injected } | undefined)?.__VAULT_E2E__?.connector;
}

export async function armE2EWalletSeam(): Promise<void> {
  // This literal `import.meta.env.VITE_E2E` test must stay the FIRST statement,
  // and must be here rather than only inside `readInjectedConnector`. Vite
  // statically replaces the expression with `false` in a production build, so
  // everything below becomes unreachable and Rollup drops it — including the
  // sole reference to `readInjectedConnector`, which then tree-shakes away and
  // takes the `__VAULT_E2E__` string literal with it.
  //
  // Verified, and not theoretical: extracting this module with ONLY the check
  // inside `readInjectedConnector` put `__VAULT_E2E__` into
  // `dist/assets/index-*.js` for the first time. Passing `import.meta.env` as a
  // VALUE defeats the substitution — Vite can only fold the direct property
  // expression, so the branch stayed live and the literal shipped. Re-run
  // `grep __VAULT_E2E__ dist/assets/*.js` after touching this file.
  if (!import.meta.env.VITE_E2E) return;
  const injected = readInjectedConnector(import.meta.env, globalThis);
  if (!injected) return;
  const { setWalletFactoriesForTests } = await import('@/lib/clients/cere-wallet/connect');
  setWalletFactoriesForTests({ connector: () => injected as never });
}
